import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { CoreResearchEntities } from 'client/data/models/core-research';
import { InventoryModel } from 'client/data/models/inventory';
import { DEFAULT_CONTENT } from 'client/data/cms/content';

import { generateSrpLinkNewMake } from 'site-modules/shared/utils/srp-link-constructor';
import { getMakeIncentivesLink } from 'site-modules/shared/utils/incentives-link-constructor';
import { getMakeHistoryContentPath } from 'site-modules/make/utils/make';
import { SCROLL_DURATION } from 'site-modules/shared/constants/sub-navigation';

import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { MakeLink } from 'site-modules/make/components/make-info/make-link';

const MAKE_LINK_PROPS = {
  className: 'bg-blue-100 h-100 rounded',
  iconContainerClassName: 'text-blue-100 bg-blue-60',
  labelClassName: 'fw-bold',
};

export function MakeInfoUI({ makeHistoryContent = DEFAULT_CONTENT, makeSlug, makeName, newInventoryCount = 0 }) {
  const fullHistory = makeHistoryContent.metadata('content').value();
  const hasNewInvLink = newInventoryCount > 0;

  return (
    <Fragment>
      <ContentCollapse
        minHeight="4.25rem" // 3 * 1.375
        btnContainerClasses="py-0 px-0_5"
        btnClasses="p-0 text-blue-50 fw-normal"
        btnSize="lg"
        btnArrowClasses="xsmall"
        btnTrackingId="make_index_history"
        justify="justify-content-start"
        textClosed="Read more"
        textOpen="Read less"
        showBtnBelow
        isEclipseFade={false}
        resetParams={{
          duration: SCROLL_DURATION,
          to: 'scrollTop-make-history',
        }}
      >
        <ContentFragment>{fullHistory}</ContentFragment>
      </ContentCollapse>

      <Row data-tracking-parent="make-index-summary" className="make-index-summary mt-2 d-flex justify-content-between">
        {hasNewInvLink && (
          <Fragment>
            <Col xs={4}>
              <MakeLink
                label={`See all new ${makeName} vehicles for sale`}
                iconClassName="icon-price-tag3"
                to={generateSrpLinkNewMake(makeSlug)}
                data-tracking-id="view_search_result"
                {...MAKE_LINK_PROPS}
              />
            </Col>
            <Col xs={4}>
              <MakeLink
                label={`Lease a new ${makeName}`}
                iconClassName="icon-appraisal"
                to={`/${makeSlug}-lease-deals/`}
                data-tracking-id="view_search_result"
                {...MAKE_LINK_PROPS}
              />
            </Col>
          </Fragment>
        )}
        <Col xs={4}>
          <MakeLink
            label={`${makeName} tax credits, rebates and incentives`}
            iconClassName="icon-coins"
            to={getMakeIncentivesLink(makeSlug)}
            data-tracking-id="link_select_tool_select"
            data-tracking-value="incentives"
            {...MAKE_LINK_PROPS}
          />
        </Col>
      </Row>
    </Fragment>
  );
}

MakeInfoUI.propTypes = {
  makeHistoryContent: CmsEntities.Content,
  makeSlug: PropTypes.string.isRequired,
  makeName: PropTypes.string.isRequired,
  submodelsList: PropTypes.arrayOf(CoreResearchEntities.VehicleSubmodel),
  pubStates: PropTypes.shape({}),
  newInventoryCount: PropTypes.number,
};

export const MakeInfo = connectToModel(MakeInfoUI, {
  makeHistoryContent: bindToContent(({ makeSlug }) => getMakeHistoryContentPath(makeSlug)),
  newInventoryCount: bindToPath('searchResults', InventoryModel, searchResults =>
    get(searchResults, 'inventories.totalNumber', 0)
  ),
});
